import { useEffect, useRef, useState } from 'react';

import { Box, SxProps, Theme } from '@mui/material';
import { DirectionsOutlined, InfoOutlined, PhoneOutlined } from '@mui/icons-material';

import { resolvePhoneNumberFormat } from '@/utils';
import { BffFacility } from '@/graphql/__generated/sdk';
import { ASPEN_GOOGLE_MAPS_BUSINESS_URL } from '@/constants';
import { getOdpUrl } from '@/utils/generateODPLinks';
import { useAppContext } from '@/context';

import CustomButton from './UI/Button/CustomButton';
import { headerStyles } from './Header.styles';
import { ExtendedFacility } from './Header';

const LocationCardActions = ({
	selectedOffice,
	onScheduleClick,
}: {
	selectedOffice: ExtendedFacility;
	onScheduleClick: (scheduleUrl: string, linkText: string) => void;
}) => {
	const [showGradient, setShowGradient] = useState(false);
	const scrollRef = useRef<HTMLDivElement | null>(null);
	const { config } = useAppContext();

	const businessUrlPath = `${selectedOffice?.address?.address1 ?? ''}, ${selectedOffice?.address?.city ?? ''}, ${
		selectedOffice?.address?.stateCode ?? ''
	} ${selectedOffice?.address?.zipCode ?? ''}`;
	const odpPageUrl = getOdpUrl(selectedOffice.address as BffFacility['address'], config);
	const isMotto = (config.name || '').toLowerCase().includes('motto');

	useEffect(() => {
		const scrollContainer = scrollRef.current;
		if (!scrollContainer) return;

		const lastButton = scrollContainer.lastElementChild;
		if (!lastButton) return;

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => setShowGradient(!entry.isIntersecting));
			},
			{ root: scrollContainer, threshold: 1 }
		);

		observer.observe(lastButton);

		return () => observer.disconnect();
	}, []);

	if (!selectedOffice) return null;

	const SCHEDULE_APPOINTMENT_BASE_PATH = config.features.otherApps.schedulingPath;
	const additionalSchedulePath = isMotto ? '/mottoaligners' : '';
	const scheduleAppointmentUrl = `/${SCHEDULE_APPOINTMENT_BASE_PATH}${additionalSchedulePath}/?fc=${selectedOffice.code}`;

	return (
		<Box sx={headerStyles.locationCardActionsRoot}>
			<Box
				sx={
					[
						headerStyles.officeButtonsRowWrapper,
						showGradient ? headerStyles.officeButtonsRowWrapperGradient : null,
					] as SxProps<Theme>
				}
			>
				<Box sx={headerStyles.officeButtonsRow} ref={scrollRef} id="office-buttons-row">
					<CustomButton
						variantType="secondary"
						size="small"
						href={`${ASPEN_GOOGLE_MAPS_BUSINESS_URL}${businessUrlPath}`}
						target="_blank"
						rel="noreferrer noopener"
						startIcon={<DirectionsOutlined sx={{ '&.MuiSvgIcon-root': { fontSize: '1.5rem' } }} />}
					>
						Directions
					</CustomButton>

					<CustomButton
						variantType="secondary"
						size="small"
						href={`tel:${
							resolvePhoneNumberFormat(
								selectedOffice?.phoneNumber ??
									selectedOffice?.technicalInfrastructure?.phoneNumber ??
									''
							) ?? ''
						}`}
						startIcon={<PhoneOutlined sx={{ '&.MuiSvgIcon-root': { fontSize: '1.5rem' } }} />}
						className="InvocaNumber"
					>
						Call
					</CustomButton>

					{!isMotto && (
						<CustomButton
							variantType="secondary"
							size="small"
							href={odpPageUrl}
							aria-label="More Info about the location"
							startIcon={<InfoOutlined sx={{ '&.MuiSvgIcon-root': { fontSize: '1.5rem' } }} />}
						>
							More Info
						</CustomButton>
					)}
				</Box>
			</Box>

			<CustomButton
				fullWidth
				variantType="primaryDefault"
				size="medium"
				href={scheduleAppointmentUrl}
				id="schedule-appointment-button"
				onClick={() => onScheduleClick(scheduleAppointmentUrl, 'Schedule')}
			>
				Schedule
			</CustomButton>
		</Box>
	);
};

export default LocationCardActions;
